import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IndianRupee, ShoppingCart, Package, Users, TrendingUp, AlertTriangle, Bell, ArrowRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from "recharts";
import api, { inr } from "../../lib/api";

const COLORS = ["#0F291E", "#D4AF37", "#1C4231", "#E7CE7E", "#6B7280", "#B8912F", "#3d6b52"];

const BADGE = {
  confirmed: "bg-gold/20 text-forest", packed: "bg-blue-100 text-blue-700",
  "out-for-delivery": "bg-orange-100 text-orange-700", delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white border border-border rounded-xl p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="w-9 h-9 rounded-lg bg-sand flex items-center justify-center text-forest"><Icon size={16} /></div>
    </div>
    <div className="font-serif text-3xl text-forest mt-3">{value}</div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="text-muted-foreground">Loading analytics…</div>;

  return (
    <div className="fade-in" data-testid="admin-dashboard">
      <h1 className="font-serif text-4xl text-forest mb-1">Dashboard</h1>
      <p className="text-muted-foreground text-sm mb-8">Sales & inventory at a glance</p>

      {data.new_orders > 0 && (
        <Link to="/admin/orders" data-testid="new-orders-alert"
          className="flex items-center justify-between gap-3 bg-forest text-cream rounded-xl px-5 py-4 mb-6 hover:bg-forest-light transition-colors">
          <span className="flex items-center gap-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-gold" />
            </span>
            <Bell size={18} className="text-gold" />
            <span className="text-sm">You have <span className="font-semibold text-gold">{data.new_orders}</span> new order{data.new_orders > 1 ? "s" : ""} awaiting action.</span>
          </span>
          <span className="text-xs uppercase tracking-widest text-gold flex items-center gap-1">View <ArrowRight size={14} /></span>
        </Link>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={IndianRupee} label="Total Revenue" value={inr(data.total_revenue)} testid="stat-revenue" />
        <StatCard icon={ShoppingCart} label="Orders" value={data.total_orders} testid="stat-orders" />
        <StatCard icon={TrendingUp} label="Avg Order Value" value={inr(data.aov)} testid="stat-aov" />
        <StatCard icon={Users} label="Customers" value={data.total_customers} testid="stat-customers" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Package} label="Products" value={data.total_products} testid="stat-products" />
        <StatCard icon={Package} label="Units in Stock" value={data.total_stock} testid="stat-stock" />
        <StatCard icon={ShoppingCart} label="Confirmed" value={data.status_counts.confirmed || 0} testid="stat-confirmed" />
        <StatCard icon={AlertTriangle} label="Low Stock Items" value={data.low_stock.length} testid="stat-lowstock" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-border rounded-xl p-6">
          <h3 className="font-serif text-xl text-forest mb-4">Revenue Trend</h3>
          {data.revenue_series.length === 0 ? <p className="text-sm text-muted-foreground py-12 text-center">No sales data yet</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.revenue_series}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => inr(v)} />
                <Line type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2.5} dot={{ fill: "#0F291E" }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white border border-border rounded-xl p-6">
          <h3 className="font-serif text-xl text-forest mb-4">Top Products by Revenue</h3>
          {data.top_products.length === 0 ? <p className="text-sm text-muted-foreground py-12 text-center">No sales data yet</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.top_products} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={110} />
                <Tooltip formatter={(v) => inr(v)} />
                <Bar dataKey="revenue" fill="#0F291E" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-border rounded-xl p-6" data-testid="recent-orders-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl text-forest">Recent Orders</h3>
            <Link to="/admin/orders" className="text-xs uppercase tracking-widest text-gold flex items-center gap-1">All Orders <ArrowRight size={13} /></Link>
          </div>
          {(!data.recent_orders || data.recent_orders.length === 0) ? (
            <p className="text-sm text-muted-foreground py-12 text-center">No orders yet</p>
          ) : (
            <div className="space-y-1">
              {data.recent_orders.map((o) => (
                <Link key={o.order_number} to="/admin/orders" data-testid={`recent-order-${o.order_number}`}
                  className="flex items-center justify-between py-2.5 border-b border-border last:border-0 hover:bg-sand/40 -mx-2 px-2 rounded-md transition-colors">
                  <div>
                    <div className="text-sm text-forest font-medium">{o.order_number}</div>
                    <div className="text-xs text-muted-foreground">{o.name} • {o.items} item{o.items > 1 ? "s" : ""} • {new Date(o.created_at).toLocaleDateString("en-IN")}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-forest">{inr(o.total)}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${BADGE[o.status] || "bg-sand"}`}>{o.status.replace(/-/g, " ")}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-border rounded-xl p-6">
          <h3 className="font-serif text-xl text-forest mb-4">Low Stock Alerts</h3>
          {data.low_stock.length === 0 ? <p className="text-sm text-muted-foreground">All products well stocked ✓</p> : (
            <div className="space-y-2 max-h-56 overflow-y-auto no-scrollbar">
              {data.low_stock.map((l, i) => (
                <div key={i} className="flex justify-between items-center text-sm border-b border-border pb-2">
                  <span className="text-forest">{l.name} <span className="text-muted-foreground">({l.variant})</span></span>
                  <span className="text-destructive font-medium">{l.stock} left</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-border rounded-xl p-6">
          <h3 className="font-serif text-xl text-forest mb-4">Products by Category</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data.category_breakdown} dataKey="products" nameKey="category" cx="50%" cy="50%" outerRadius={80} label={(e) => e.category}>
                {data.category_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-border rounded-xl p-6">
          <h3 className="font-serif text-xl text-forest mb-4">Order Status Breakdown</h3>
          <div className="space-y-3 pt-2">
            {Object.keys(data.status_counts).length === 0 ? <p className="text-sm text-muted-foreground">No orders yet</p> :
              Object.entries(data.status_counts).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${BADGE[status] || "bg-sand"}`}>{status.replace(/-/g, " ")}</span>
                  <span className="text-forest font-medium">{count}</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
